import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-10-02 09:40:33 +0200 (Fri, 02 Oct 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.juenger $
//  Revision  : $Rev: 166189 $
//  Purpose   : custom buttons
// =============================================================================
//
// ---Third-party imports
import React, { useRef, useState, useEffect } from 'react';
import { Loader } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { Button } from 'uix/components';
import { Sel } from 'uix/registries';
import { useDelay, useInView, useSelector } from 'uix/hooks';
// ---Local imports
import S from './mediaplayer.css';
import { AudioWaves, GhostImage } from './index';
const getTypeFromSrc = (src)=>{
    if (!src) return null;
    // Extract file extension from URL or filename
    const extension = src.split('.').pop().toLowerCase().split('?')[0];
    const mimeTypeMap = {
        // Audio formats
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
        aac: 'audio/aac',
        flac: 'audio/flac',
        m4a: 'audio/mp4',
        wma: 'audio/x-ms-wma',
        webm: 'audio/webm',
        // Video formats
        mp4: 'video/mp4',
        avi: 'video/x-msvideo',
        mov: 'video/quicktime',
        wmv: 'video/x-ms-wmv',
        flv: 'video/x-flv',
        mkv: 'video/x-matroska',
        webm: 'video/webm',
        ogv: 'video/ogg',
        m4v: 'video/mp4'
    };
    return mimeTypeMap[extension] || null;
};
const SEEK_STEP = 5 // seconds per arrow key
;
const formatTime = (seconds)=>new Date((seconds || 0) * 1000).toISOString().substr(11, 8);
const MediaPlayer = ({ mode, src, ...props })=>{
    let mediaRef = useRef();
    let [inViewRef, inView] = useInView({
        triggerOnce: false,
        threshold: 0.1
    });
    let [playerState, setPlayerState] = useState({
        muted: false,
        playing: false,
        ended: false,
        paused: false,
        progress: 0,
        duration: 0,
        currentTime: 0,
        hasStarted: false
    });
    let { muted, playing, ended, duration, currentTime, hasStarted } = playerState;
    let setPlayer = (props)=>{
        return setPlayerState({
            ...playerState,
            ...props
        });
    };
    // calculates percentage for progressbar
    const progressOnTimeUpdate = ()=>{
        const { currentTime, duration } = mediaRef?.current;
        const progressPercentage = currentTime / duration * 100;
        setPlayer({
            progress: progressPercentage,
            duration: duration,
            currentTime: currentTime
        });
    };
    // media sources
    // Auto-determine if this is embedded content (external URL)
    let isEmbedded = src && (src.startsWith('http://') || src.startsWith('https://'));
    let mediaType = getTypeFromSrc(src);
    //resets player after full playback
    useDelay(()=>{
        let ref = mediaRef?.current;
        if (ref) {
            ref.currentTime = 0;
        }
        setPlayer({
            currentTime: 0,
            playing: false,
            progress: 0,
            ended: false,
            hasStarted: false
        });
    }, [
        1500
    ], [
        ended
    ]);
    useEffect(()=>{
        let ref = mediaRef?.current;
        if (ref) {
            ref.muted = muted;
        }
    }, [
        muted
    ]);
    // player event handler
    const endHandler = ()=>{
        let ref = mediaRef?.current;
        if (ref) {
            setPlayer({
                ended: true
            });
        }
    };
    const muteHandler = ()=>{
        setPlayer({
            muted: !muted
        });
    };
    const loadHandler = ()=>{
        let ref = mediaRef?.current;
        setPlayer({
            duration: ref.duration,
            loaded: true
        });
    };
    const playAudio = ()=>{
        setPlayer({
            playing: true,
            paused: false,
            hasStarted: true
        });
        mediaRef?.current.play();
    };
    const pauseAudio = ()=>{
        setPlayer({
            paused: true,
            playing: false
        });
        mediaRef?.current.pause();
    };
    const playerHandler = ()=>{
        playing ? pauseAudio() : playAudio();
    };
    //!TODO: datefn
    // calculate remaining time on playback
    let remainingTime = formatTime(duration ? duration - currentTime : 0);
    if (mode === 'audio') {
        return /*#__PURE__*/ _jsx(AudioPlayer, {
            endHandler: endHandler,
            inView: inView,
            inViewRef: inViewRef,
            loadHandler: loadHandler,
            mediaRef: mediaRef,
            muteHandler: muteHandler,
            playerHandler: playerHandler,
            playerState: playerState,
            progressOnTimeUpdate: progressOnTimeUpdate,
            remainingTime: remainingTime,
            setPlayer: setPlayer,
            src: src,
            type: mediaType,
            ...props
        });
    } else {
        return /*#__PURE__*/ _jsx(VideoPlayer, {
            isEmbedded: isEmbedded,
            endHandler: endHandler,
            inView: inView,
            inViewRef: inViewRef,
            loadHandler: loadHandler,
            mediaRef: mediaRef,
            playerHandler: playerHandler,
            playerState: playerState,
            progressOnTimeUpdate: progressOnTimeUpdate,
            remainingTime: remainingTime,
            setPlayer: setPlayer,
            src: src,
            type: mediaType,
            ...props
        });
    }
};
const ProgressBar = ({ playerState, setPlayer, mediaRef })=>{
    let progressRef = useRef();
    let i18n = useSelector(Sel.i18n);
    let { duration, progress, currentTime } = playerState;
    // progressbar click changes playback position
    const handleClickProgressBar = (e)=>{
        let clickPosition = e.pageX - progressRef.current.getBoundingClientRect().x;
        let width = progressRef.current.offsetWidth;
        let progressPercentage = clickPosition / width * 100;
        let updatedCurrentTime = duration / 100 * progressPercentage;
        mediaRef.current.currentTime = updatedCurrentTime;
        setPlayer({
            progress: progressPercentage,
            currentTime: updatedCurrentTime
        });
    };
    // keyboard seeking like a native slider
    const handleKeyDown = (e)=>{
        let target = {
            ArrowRight: currentTime + SEEK_STEP,
            ArrowUp: currentTime + SEEK_STEP,
            ArrowLeft: currentTime - SEEK_STEP,
            ArrowDown: currentTime - SEEK_STEP,
            Home: 0,
            End: duration
        }[e.key];
        if (target === undefined || !duration) return;
        e.preventDefault();
        let time = Math.min(Math.max(target, 0), duration);
        mediaRef.current.currentTime = time;
        setPlayer({
            progress: time / duration * 100,
            currentTime: time
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        role: "slider",
        tabIndex: 0,
        "aria-label": i18n.get('pres_media_position', 'Wiedergabeposition'),
        "aria-valuemin": 0,
        "aria-valuemax": Math.round(duration || 0),
        "aria-valuenow": Math.round(currentTime || 0),
        "aria-valuetext": `${formatTime(currentTime)} / ${formatTime(duration)}`,
        onClick: (e)=>handleClickProgressBar(e),
        onKeyDown: handleKeyDown,
        ref: progressRef,
        className: S.progressBarCnt,
        children: /*#__PURE__*/ _jsx("div", {
            className: S.progressBar,
            style: {
                width: `${progress}%`
            }
        })
    });
};
const AudioPlayer = ({ caption, endHandler, inView, inViewRef, loadHandler, mediaRef, muteHandler, playerHandler, playerState, preload, progressOnTimeUpdate, remainingTime, setPlayer, src, type, refid })=>{
    let [sceneRef, sceneInView] = useInView({
        triggerOnce: false
    });
    let [sticky, setSticky] = useState(false);
    let i18n = useSelector(Sel.i18n);
    let { muted, playing, currentTime } = playerState;
    //adds sticky-player when playing or already played
    useEffect(()=>{
        let ref = mediaRef?.current;
        if (ref && !sceneInView && !inView) {
            setSticky(false);
            ref.pause();
            setPlayer({
                paused: true,
                playing: false
            });
        }
        if (inView) {
            setSticky(false);
        }
        if (!sticky) {
            if (sceneInView && !inView && (currentTime || playerState.playing)) {
                setSticky(true);
            }
        }
    }, [
        inView,
        sceneInView
    ]);
    let progressBar = /*#__PURE__*/ _jsx(ProgressBar, {
        mediaRef: mediaRef,
        setPlayer: setPlayer,
        playerState: playerState
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: S.sceneHeight,
                ref: sceneRef
            }),
            /*#__PURE__*/ _jsx("div", {
                className: S.visibleAnchor,
                ref: inViewRef
            }),
            /*#__PURE__*/ _jsx("div", {
                refid: refid,
                className: clsx(S.playerContainer, sticky && S.sticky),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: S.audioPlayer,
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: S.center,
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    className: S.playButton,
                                    style: {
                                        flex: 'none',
                                        color: 'var(--presentation_themeColor)',
                                        border: '1px solid var(--presentation_themeColor)'
                                    },
                                    inverted: true,
                                    icon: playing ? 'pause' : 'play',
                                    ariaLabel: playing ? i18n.get('pres_media_pause', 'Pause') : i18n.get('pres_media_play', 'Abspielen'),
                                    onClick: playerHandler
                                }),
                                caption ? /*#__PURE__*/ _jsx("div", {
                                    className: S.caption,
                                    children: caption
                                }) : progressBar,
                                /*#__PURE__*/ _jsx("div", {
                                    className: S.audioWavesCnt,
                                    children: /*#__PURE__*/ _jsx(AudioWaves, {
                                        className: S.videoWaves,
                                        hidden: false,
                                        onClick: muteHandler,
                                        ariaLabel: i18n.get('pres_media_mute', 'Stummschalten'),
                                        active: playing,
                                        muted: muted
                                    })
                                })
                            ]
                        }),
                        caption && progressBar,
                        /*#__PURE__*/ _jsx("div", {
                            className: S.remaining,
                            children: remainingTime
                        }),
                        /*#__PURE__*/ _jsx("audio", {
                            onLoadedData: loadHandler,
                            onEnded: endHandler,
                            onTimeUpdate: progressOnTimeUpdate,
                            ref: mediaRef,
                            preload: preload,
                            children: /*#__PURE__*/ _jsx("source", {
                                src: src,
                                type: type || 'audio/mpeg'
                            })
                        })
                    ]
                })
            })
        ]
    });
};
const VideoPlayer = ({ caption, consent = true, isEmbedded, endHandler, height = '360px', id, inView, inViewRef, loadHandler, mediaRef, playerHandler, playerState, poster, preload = 'metadata', progressOnTimeUpdate, remainingTime, setPlayer, src, type, refid, ...props })=>{
    let apiurl = useSelector(Sel.apiurl);
    let i18n = useSelector(Sel.i18n);
    let { wikipaneId } = props;
    let [consentGiven, setConsentGiven] = useState(false);
    let [hovered, setHovered] = useState(false);
    let [posterError, setPosterError] = useState(false);
    let { playing, currentTime, paused, loaded, hasStarted } = playerState;
    if (!src && !isEmbedded) {
        log.warn(`Video source not found on ${wikipaneId}`);
    }
    // Handle poster URL - if poster is id, use apiurl.href, otherwise use URL
    let videoPoster;
    if (poster && !isEmbedded) {
        if (!isNaN(poster)) {
            videoPoster = apiurl.href(`/download/webresource/${poster}`); // Use API URL for numeric IDs
        } else {
            videoPoster = poster; // Use direct URL for non-numeric values
        }
    } else {
        videoPoster = undefined;
    }
    // Only preload inView
    let videoPreload = inView || loaded || hasStarted ? preload : 'none';
    useEffect(()=>{
        if (!inView) {
            let ref = mediaRef?.current;
            if (ref && !isEmbedded) {
                ref.pause();
                setPlayer({
                    paused: true,
                    playing: false
                });
            }
        }
    }, [
        inView
    ]);
    const toggleFullscreen = ()=>{
        let el = mediaRef.current;
        if (!el) return console.warn('Video element not found');
        if (el.requestFullscreen) {
            el.requestFullscreen();
        } else if (el.msRequestFullscreen) {
            el.msRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
            el.mozRequestFullScreen();
        } else if (el.webkitRequestFullscreen) {
            el.webkitRequestFullscreen();
        }
    };
    if (!isEmbedded) {
        src = apiurl.href(src);
    } // else external url (probably youtube)
    let hideControls = !loaded || !hovered;
    let onCanPlayHandler = ()=>{
        let ref = mediaRef?.current;
        if (ref) {
            setPlayer({
                loaded: true,
                duration: ref.duration
            });
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                refid: refid,
                ref: inViewRef,
                className: clsx(S.videoPlayer, loaded && S.loaded, isEmbedded && S.embeddedPlayer),
                onMouseEnter: ()=>setHovered(true),
                onMouseLeave: ()=>setHovered(false),
                children: !isEmbedded ? /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: S.videoWrapper,
                        children: [
                            (!videoPoster || posterError) && /*#__PURE__*/ _jsx("div", {
                                className: clsx(S.ghostImageWrapper, loaded ? S.loaded : S.loading),
                                children: /*#__PURE__*/ _jsx(GhostImage, {
                                    className: S.ghostImage,
                                    height: height
                                })
                            }),
                            videoPoster && !hasStarted && !posterError && /*#__PURE__*/ _jsx("img", {
                                src: videoPoster,
                                onError: ()=>{
                                    log.error('Failed to load poster image: ' + videoPoster);
                                    setPosterError(true);
                                },
                                className: clsx(S.videoPoster, loaded ? S.loaded : S.loading),
                                onClick: playerHandler
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                className: clsx(S.playButton, S.videoPlayButton, (playing || !loaded && (!videoPoster || posterError)) && S.hide),
                                inverted: true,
                                size: "huge",
                                icon: paused || currentTime == 0 ? 'play' : 'pause',
                                ariaLabel: paused || currentTime == 0 ? i18n.get('pres_media_play', 'Abspielen') : i18n.get('pres_media_pause', 'Pause'),
                                onClick: playerHandler
                            }),
                            /*#__PURE__*/ _jsx("video", {
                                id: id,
                                onClick: playerHandler,
                                className: clsx(loaded ? S.loaded : S.loading, hasStarted || loaded || !videoPoster || posterError ? S.visible : S.hidden),
                                onCanPlay: onCanPlayHandler,
                                onLoadedData: loadHandler,
                                onEnded: endHandler,
                                onTimeUpdate: progressOnTimeUpdate,
                                ref: mediaRef,
                                preload: videoPreload,
                                children: /*#__PURE__*/ _jsx("source", {
                                    src: src,
                                    type: type || 'video/mp4'
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: clsx(S.bottomCnt, S.bottomCnt, !hovered && S.hide),
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: S.captions,
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: S.remaining,
                                            children: remainingTime
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        className: S.fullscreenButton,
                                        icon: "expand",
                                        ariaLabel: i18n.get('pres_media_fullscreen', 'Vollbild'),
                                        onClick: toggleFullscreen
                                    }),
                                    /*#__PURE__*/ _jsx(ProgressBar, {
                                        mediaRef: mediaRef,
                                        setPlayer: setPlayer,
                                        playerState: playerState
                                    })
                                ]
                            })
                        ]
                    })
                }) : /*#__PURE__*/ _jsx("div", {
                    children: !consent || consentGiven ? /*#__PURE__*/ _jsx("iframe", {
                        frameBorder: "0",
                        ref: mediaRef,
                        src: src,
                        wmode: "transparent",
                        type: "video/mp4",
                        width: "100%",
                        height: height,
                        allowFullScreen: true
                    }) : /*#__PURE__*/ _jsx(ConsentOverlay, {
                        setConsent: setConsentGiven,
                        height: height,
                        i18n: i18n
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: S.captionWrapper,
                children: caption && !isEmbedded && /*#__PURE__*/ _jsx("div", {
                    className: S.caption,
                    children: caption
                })
            })
        ]
    });
};
const ConsentOverlay = ({ height, setConsent, i18n })=>{
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: S.consentOverlay,
            style: {
                height: height
            },
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    disabled: true,
                    className: S.consentPlayButton,
                    inverted: true,
                    size: "giant",
                    icon: "play"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: S.content,
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: i18n.get('other.videoConsent')
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            className: S.acceptButton,
                            size: "small",
                            inverted: true,
                            content: "Akzeptieren und Video laden",
                            circle: false,
                            onClick: ()=>setConsent(true)
                        })
                    ]
                })
            ]
        })
    });
};
MediaPlayer.Audio = ({ ...props })=>/*#__PURE__*/ _jsx(MediaPlayer, {
        mode: "audio",
        ...props
    });
MediaPlayer.Video = ({ ...props })=>/*#__PURE__*/ _jsx(MediaPlayer, {
        mode: "video",
        ...props
    });
export { MediaPlayer, ProgressBar };
